import React, { useState } from "react";
import { getData } from "country-list";
import Modal from "../components/Modal";
import { Button, Field, Input, Select, Textarea } from "../components/ui";
import { useClubs } from "../apiCalls/clubApiCalls";
import { useCreatePlayer, useUpdatePlayer } from "../apiCalls/playerApiCalls";

// ISO alpha-2 codes — what the API stores and what the flags render from.
const countries = getData();

const blankPlayer = {
  name: "",
  gender: "Male",
  nationality: "AE",
  dateOfBirth: "",
  club: "",
  description: "",
  status: "active",
};

// One form for both adding and editing. `player` null means add.
export default function PlayerForm({ player, onClose }) {
  const isEdit = Boolean(player);

  const [form, setForm] = useState(() =>
    isEdit
      ? {
          name: player.name || "",
          gender: player.gender || "Male",
          nationality: player.nationality || "",
          dateOfBirth: player.dateOfBirth?.slice(0, 10) || "",
          club: player.club?._id || "",
          description: player.description || "",
          status: player.status || "active",
        }
      : blankPlayer
  );

  const { data: clubs } = useClubs();
  const { mutate: create, isLoading: isCreating } = useCreatePlayer();
  const { mutate: update, isLoading: isUpdating } = useUpdatePlayer();
  const isSaving = isCreating || isUpdating;

  const set = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  const handleSubmit = (event) => {
    event.preventDefault();

    // Send null rather than "" so the API stores an absent club as absent.
    const payload = { ...form, club: form.club || null };
    const onSuccess = () => onClose();

    if (isEdit) update({ id: player._id, ...payload }, { onSuccess });
    else create(payload, { onSuccess });
  };

  return (
    <Modal
      title={isEdit ? `Edit ${player.name}` : "Add a player"}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            form="player-form"
            variant="primary"
            disabled={isSaving}
          >
            {isSaving ? "Saving" : isEdit ? "Save changes" : "Add player"}
          </Button>
        </>
      }
    >
      <form
        id="player-form"
        onSubmit={handleSubmit}
        className="grid gap-5 sm:grid-cols-2"
      >
        <div className="sm:col-span-2">
          <Field
            label="Full name"
            hint="As it should appear in the rankings and on the draw sheet."
          >
            <Input value={form.name} onChange={set("name")} required autoFocus />
          </Field>
        </div>

        <Field label="Gender">
          <Select value={form.gender} onChange={set("gender")} required>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </Select>
        </Field>

        <Field label="Nationality">
          <Select value={form.nationality} onChange={set("nationality")}>
            <option value="">Not recorded</option>
            {countries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Date of birth">
          <Input
            type="date"
            value={form.dateOfBirth}
            onChange={set("dateOfBirth")}
          />
        </Field>

        <Field label="Club">
          <Select value={form.club} onChange={set("club")}>
            <option value="">Independent</option>
            {(clubs || []).map((club) => (
              <option key={club._id} value={club._id}>
                {club.name}
              </option>
            ))}
          </Select>
        </Field>

        <div className="sm:col-span-2">
          <Field
            label="Status"
            hint="Only active players appear in the public rankings."
          >
            <Select value={form.status} onChange={set("status")}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
          </Field>
        </div>

        <div className="sm:col-span-2">
          <Field label="Bio">
            <Textarea
              rows={3}
              value={form.description}
              onChange={set("description")}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}
